import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { get } from "../../../services/api-services";
import WeightProgress from "./Weight";
import UserSupportTopics from "./Support";
import UserImageLibrary from "./Gallery";
import BasicInfo from "./BasicInfo";
import PerformanceOverview from "./PerformanceOverview";
import { useCommon } from "../../../context/CommonContext";

const GenrelInfo = ({ userData, fetchUserData, userEnrollment, handleSectionChange }) => {
    const { setSnackbar } = useCommon();
    const navigate = useNavigate();
    const [weightData, setWeightData] = useState({
        id: null,
        height: null,
        startWeight: null,
        currentWeight: null,
        targetWeight: null,
        manualTargetWeight: "",
        startWeightBMI: null,
        currentWeightBMI: null,
        targetWeightBMI: null,
        weightGoalPercent: 0,
    });
    const weightGoalParcent = async (height, gender, dob, newTarget, startWeight, currentWeight) => {
        console.log("weightGoalParcent input:", { height, gender, dob, newTarget, startWeight, currentWeight });
        let targetWeight = 0;
        let userWeightParcent = 0;

        if (height !== undefined && gender !== undefined && startWeight !== undefined && startWeight !== 0 && currentWeight !== undefined && currentWeight !== 0) {
            let idealWeight = 0;
            const BMIValue = startWeight / ((height / 100) * (height / 100));
            if (BMIValue < 18.5) {
                idealWeight = Math.round(18.5 * ((height / 100) * (height / 100)));
            } else if (BMIValue >= 18.5 && BMIValue < 24.9) {
                idealWeight = Math.round(22.5 * ((height / 100) * (height / 100)));
            } else {
                // Using 24 BMI to match Weight.jsx overweight/obese ideal weight suggestion of 80 Kgs for 183 Cms
                idealWeight = Math.round(24 * ((height / 100) * (height / 100)));
            }
            const today = new Date();
            const nowyear = today.getFullYear();
            const db = new Date(dob);
            const birthyear = db.getFullYear();
            let age = nowyear - birthyear;
            age = age - 1;
            if (!newTarget || newTarget === "0" || Number(newTarget) === 0) {
                targetWeight = idealWeight;
            } else {
                targetWeight = Number(newTarget);
            }
            if (targetWeight === startWeight) {
                userWeightParcent = 0;
            } else {
                userWeightParcent = Math.round(((currentWeight - startWeight) / (targetWeight - startWeight)) * 100);
            }
        }
        return { targetWeight, userWeightParcent }
    };
    const getWeightData = async () => {
        const url = `/Customers/${id}`;
        try {
            const response = await get(url);
            const data = response.data;
            if (data) {
                const startWeight = parseFloat(data.weight) || 0;
                const height = parseFloat(data.height);
                const heightInMeters = height / 100;
                if (isNaN(heightInMeters) || heightInMeters === 0) {
                    return;
                }
                calculateBMI(startWeight, startWeight, height);
            } else {
                setWeightData({
                    startWeight: 0,
                    currentWeight: 0,
                    targetWeight: 0,
                    manualTargetWeight: "",
                    startWeightBMI: 0,
                    currentWeightBMI: 0,
                    weightGoalPercent: 0,
                });
            }
        } catch (error) {
            if (error.response?.status === 401) {
                setSnackbar({
                    openSnackbar: true,
                    message: 'Login expired, please login again.',
                    severity: 'error'
                });
                localStorage.clear();
                navigate("/login");
            } else {
                console.error("Error fetching weight data", error);
            }
        }
    };
    const getCurrentWeight = async () => {
        try {
            const url = `/Measurements?filter={"where": {"customerId": "${id}", "type": "weight"}, "order" : "recordedAt DESC"}`;
            const response = await get(url);
            const customerUrl = `/Customers/${id}`;
            const customerRes = await get(customerUrl);

            if (response.data && response.data.length > 0) {
                const startWeight = response.data.slice(-1)[0].value;
                const currentWeight = response.data[0].value;
                const gender = customerRes?.data?.gender;
                const dob = customerRes?.data?.dateOfBirth;
                const newTarget = customerRes?.data?.weightGoal;
                const height = customerRes?.data?.height;
                const { targetWeight, userWeightParcent } = await weightGoalParcent(height, gender, dob, newTarget, Number(startWeight), currentWeight);
                const { currentWeightBMI, startWeightBMI } = calculateBMI(startWeight, currentWeight, height);
                setWeightData((prev) => ({
                    ...prev,
                    customerId: customerRes?.data?.id,
                    currentWeight,
                    startWeight,
                    currentWeightBMI,
                    startWeightBMI,
                    weightGoalPercent: userWeightParcent,
                    targetWeight,
                    manualTargetWeight: newTarget || "",
                    height
                }));
            } else {
                console.log("No current weight data found");
            }
        } catch (error) {
            console.error("Error fetching current weight", error);
        }
    };
    const calculateBMI = (startWeight,currentWeight, height) => {
        if (!startWeight || !currentWeight || !height) return 0;
        let num1 = (currentWeight / ((height/100) * (height/100)));
        let currentWeightBMI = parseFloat(num1.toString()).toFixed(1);
      //  let num = (startWeight / ((height/100) * (height/100)));
        // let startWeightBMI = parseFloat(num.toString()).toFixed(1);
        let startWeightBMI = currentWeightBMI;
        return {currentWeightBMI,startWeightBMI }
    };
    const { id } = useParams();
    const [openAddWeight, setOpenAddWeight] = useState(false);
    const [performanceMetrics, setPerformanceMetrics] = useState({
        caloriesBurnt: 0,
        activityScore: 0,
        streak: 0,
        bestStreak: 0,
        currentDay: 1
    });

    const fetchPerformanceData = async () => {
        try {
            const filter = {
                where: {
                    or: [
                        { customerId: id },
                        { userId: id }
                    ]
                },
                include: 'dailyTask',
                order: 'date DESC',
                limit: 100
            };
            const res = await get(`/TaskLogs?filter=${encodeURIComponent(JSON.stringify(filter))}`);
            
            if (res?.data) {
                const logs = res.data;
                
                // Helper for Local Date (YYYY-MM-DD) - Fixes Timezone Bug
                const getLocalDateStr = (date) => {
                    const d = new Date(date);
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                };

                // Group by local date
                const dailyStats = {};
                logs.forEach(log => {
                    const dateStr = getLocalDateStr(log.date);
                    if (!dailyStats[dateStr]) {
                        dailyStats[dateStr] = { completed: 0, total: 0, calories: 0, hasActivity: false };
                    }
                    
                    const taskCalories = log.dailyTask?.calories || 0;
                    dailyStats[dateStr].total += 1;
                    
                    if (log.status === 'completed') {
                        dailyStats[dateStr].completed += 1;
                        dailyStats[dateStr].calories += taskCalories;
                        dailyStats[dateStr].hasActivity = true;
                    } else if (log.status === 'partial') {
                        dailyStats[dateStr].completed += 0.5;
                        dailyStats[dateStr].calories += (taskCalories / 2);
                        dailyStats[dateStr].hasActivity = true;
                    } else if (log.value || log.userComment) {
                        dailyStats[dateStr].hasActivity = true;
                    }
                });

                // Calculate Activity Score and Calories Burnt (Today's values)
                const today = new Date();
                const todayStr = today.toISOString().split('T')[0];
                const todayLogs = logs.filter(log => {
                    const logDate = new Date(log.date).toISOString().split('T')[0];
                    return logDate === todayStr;
                });

                let todayActivityScore = 0;
                let todayCaloriesBurnt = 0;

                if (todayLogs.length > 0) {
                    const latestLog = todayLogs[0];
                    const tasks = latestLog.tasks || [];
                    const completedTasks = tasks.filter(t => t.status === 'completed').length;
                    todayActivityScore = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;
                    todayCaloriesBurnt = tasks.reduce((sum, t) => sum + (t.status === 'completed' ? (t.calories || 0) : 0), 0);
                }
                
                // Robust Streak & Best Streak Calculation
                let currentStreak = 0;
                let bestStreak = 0;
                
                const activityDates = Object.keys(dailyStats)
                    .filter(date => dailyStats[date].hasActivity)
                    .sort((a, b) => new Date(b) - new Date(a));

                if (activityDates.length > 0) {
                    const yesterday = new Date(today);
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = getLocalDateStr(yesterday);
                    const todayLocalStr = getLocalDateStr(today);
                    
                    const latestLogDateStr = activityDates[0];
                    const isActive = latestLogDateStr === todayLocalStr || latestLogDateStr === yesterdayStr;

                    if (isActive) {
                        currentStreak = 1;
                        let lastDate = new Date(latestLogDateStr);
                        for (let i = 1; i < activityDates.length; i++) {
                            const currentDate = new Date(activityDates[i]);
                            const diffDays = Math.round((lastDate - currentDate) / (1000 * 60 * 60 * 24));
                            if (diffDays === 1) {
                                currentStreak++;
                                lastDate = currentDate;
                            } else {
                                break;
                            }
                        }
                    }

                    // Calculate Best Streak across history
                    let historyBest = 0;
                    let currentRun = 0;
                    const sortedAsc = [...activityDates].sort((a, b) => new Date(a) - new Date(b));
                    
                    if (sortedAsc.length > 0) {
                        currentRun = 1;
                        historyBest = 1;
                        for (let i = 1; i < sortedAsc.length; i++) {
                            const prev = new Date(sortedAsc[i-1]);
                            const curr = new Date(sortedAsc[i]);
                            const diff = Math.round((curr - prev) / (1000 * 60 * 60 * 24));
                            if (diff === 1) {
                                currentRun++;
                            } else {
                                currentRun = 1;
                            }
                            if (currentRun > historyBest) historyBest = currentRun;
                        }
                    }
                    bestStreak = historyBest;
                }

                // Stage & Progress
                const finalStreak = currentStreak; 

                // Program Progress (Based on Registration Date)
                const activeEnrollment = userEnrollment?.find(e => e.status === 'active') || userEnrollment?.[0];
                let programDay = 1;
                if (userData?.createdAt) {
                    const start = new Date(userData.createdAt);
                    start.setHours(0, 0, 0, 0);
                    const todayCopy = new Date(today);
                    todayCopy.setHours(0, 0, 0, 0);
                    programDay = Math.max(1, Math.round((todayCopy - start) / (1000 * 60 * 60 * 24)) + 1);
                }

                setPerformanceMetrics({
                    caloriesBurnt: todayCaloriesBurnt,
                    activityScore: todayActivityScore,
                    streak: finalStreak,
                    bestStreak: Math.max(bestStreak, activeEnrollment?.bestStreak || 0, programDay),
                    currentDay: programDay, 
                    stage: Math.min(Math.floor((programDay - 1) / 7) + 1, 4),
                    programDay: programDay
                });
            }
        } catch (error) {
            console.error("Error fetching performance data", error);
        }
    };

    useEffect(() => {
        if (id) {
            fetchPerformanceData();
        }
    }, [id, userEnrollment, userData]);


    return (
        <div className="flex flex-col xl:flex-row flex-1 gap-4">
            <div className="xl:w-[40%] w-full">
                <BasicInfo handleSectionChange={handleSectionChange} weightGoalParcent={weightGoalParcent} customerId={id} userData={userData} fetchUserData={fetchUserData} userEnrollment={userEnrollment} setWeightData={setWeightData} getWeightData={getWeightData} getCurrentWeight={getCurrentWeight}/>
            </div>
            <div className="flex flex-col xl:w-[60%] w-full gap-5">
                <div className="flex flex-col xl:flex-row gap-4">
                    <WeightProgress
                        openAddWeight={openAddWeight}
                        userData={userData}
                        setOpenAddWeight={setOpenAddWeight}
                        weightData={weightData}
                        setWeightData={setWeightData}
                        calculateBMI={calculateBMI}
                        getCurrentWeight={getCurrentWeight} 
                        fetchUserData={fetchUserData}
                        weightGoalParcent={weightGoalParcent}
                    />
                    <UserSupportTopics />
                </div>
                <PerformanceOverview metrics={performanceMetrics} />
                <UserImageLibrary />
            </div>
        </div>
    )
}

export default GenrelInfo;